import React, { useState, useEffect } from 'react';
import {
  Users,
  TrendingUp,
  DollarSign,
  TreePine,
  BarChart3,
  Settings,
  Plus,
  Edit,
  Trash2,
  Eye,
  Award,
  Target,
  Gift,
  Share2,
  Copy,
  Download,
  Filter,
  Search,
  ChevronDown,
  ChevronRight,
  Crown,
  Star,
  Zap,
  Building2,
  UserCheck,
  Wallet,
  Activity,
  PieChart,
  LineChart,
  Car,
  Server,
  CheckCircle2,
  Clock3,
  Percent
} from 'lucide-react';

// 타입 정의
interface Distributor {
  id: string;
  walletAddress: string;
  nickname: string;
  foundationCode: string; // A8888888
  personalCode: string; // 지갑 뒷 8자리
  tier: 'DEALER' | 'AGENCY' | 'DISTRIBUTOR';
  tierLevel: number; // 1: Dealer, 2: Agency, 3: Distributor
  totalSales: number;
  totalCommissions: number;
  commissionRate: number;
  nodeRewards: number;
  carEligible: boolean;
  directReferrals: number;
  totalReferrals: number;
  isActive: boolean;
  createdAt: string;
  lastActivity: string;
}

interface ReferralHierarchy {
  distributor: Distributor;
  children: ReferralHierarchy[];
  level: number;
  totalSales: number;
  totalCommissions: number;
}

interface Commission {
  id: string;
  distributorId: string;
  referrerAddress: string;
  referredAddress: string;
  salesAmount: number;
  commissionRate: number;
  commissionUSDT: number;
  commissionVTTN: number;
  status: 'pending' | 'paid' | 'failed';
  createdAt: string;
  paidAt?: string;
}

interface NodeSale {
  id: string;
  sellerAddress: string;
  buyerAddress: string;
  nodeCount: number;
  totalPrice: number;
  commissionRate: number;
  commissionUSDT: number;
  commissionVTTN: number;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
}

interface DashboardStats {
  totalDistributors: number;
  activeDistributors: number;
  totalSales: number;
  totalCommissions: number;
  totalNodesSold: number;
  pendingPayouts: number;
  tierDistribution: {
    dealer: number;
    agency: number;
    distributor: number;
  };
}

const ReferralManagementPage: React.FC = () => {
  // 상태 관리
  const [activeTab, setActiveTab] = useState('dashboard');
  const [boomEventSettings, setBoomEventSettings] = useState({
    boom1Visible: true,
    boom2Visible: true
  });
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [hierarchy, setHierarchy] = useState<ReferralHierarchy[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [nodeSales, setNodeSales] = useState<NodeSale[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  
  // 코드 조회 관련 상태
  const [codeSearchTerm, setCodeSearchTerm] = useState('');
  const [codeSearchType, setCodeSearchType] = useState<'foundation' | 'personal' | 'all'>('all');
  const [codeSearchResults, setCodeSearchResults] = useState<Distributor[]>([]);
  const [selectedCodeResult, setSelectedCodeResult] = useState<Distributor | null>(null);
  const [showCodeTreeModal, setShowCodeTreeModal] = useState(false);
  
  // 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);

  // 폼 상태
  const [newDistributor, setNewDistributor] = useState({
    walletAddress: '',
    nickname: '',
    tier: 'DEALER' as const,
    commissionRate: 0.1
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Mock 데이터 생성 (실제로는 API 호출)
      const mockDistributors: Distributor[] = [
        {
          id: 'dist-1',
          walletAddress: '0x1234567890123456789012345678901234567890',
          nickname: '총판 김철수',
          foundationCode: 'A8888888',
          personalCode: '12345678',
          tier: 'DISTRIBUTOR',
          tierLevel: 3,
          totalSales: 3500000000, // 35억
          totalCommissions: 1050000000, // 10.5억
          commissionRate: 0.30,
          nodeRewards: 6,
          carEligible: true,
          directReferrals: 25,
          totalReferrals: 150,
          isActive: true,
          createdAt: '2024-01-15T10:00:00Z',
          lastActivity: '2024-09-15T14:30:00Z'
        },
        {
          id: 'dist-2',
          walletAddress: '0x2345678901234567890123456789012345678901',
          nickname: '대리점 이영희',
          foundationCode: 'A8888888',
          personalCode: '23456789',
          tier: 'AGENCY',
          tierLevel: 2,
          totalSales: 1800000000, // 18억
          totalCommissions: 360000000, // 3.6억
          commissionRate: 0.20,
          nodeRewards: 3,
          carEligible: false,
          directReferrals: 15,
          totalReferrals: 75,
          isActive: true,
          createdAt: '2024-02-20T09:00:00Z',
          lastActivity: '2024-09-15T12:15:00Z'
        },
        {
          id: 'dist-3',
          walletAddress: '0x3456789012345678901234567890123456789012',
          nickname: '딜러 박민수',
          foundationCode: 'A8888888',
          personalCode: '34567890',
          tier: 'DEALER',
          tierLevel: 1,
          totalSales: 600000000, // 6억
          totalCommissions: 60000000, // 6천만
          commissionRate: 0.10,
          nodeRewards: 1,
          carEligible: false,
          directReferrals: 8,
          totalReferrals: 25,
          isActive: true,
          createdAt: '2024-03-10T14:00:00Z',
          lastActivity: '2024-09-15T11:45:00Z'
        },
        {
          id: 'dist-4',
          walletAddress: '0x4567890123456789012345678901234567890123',
          nickname: '총판 정수진',
          foundationCode: 'A8888888',
          personalCode: '45678901',
          tier: 'DISTRIBUTOR',
          tierLevel: 3,
          totalSales: 4200000000, // 42억
          totalCommissions: 1260000000, // 12.6억
          commissionRate: 0.30,
          nodeRewards: 6,
          carEligible: true,
          directReferrals: 30,
          totalReferrals: 180,
          isActive: true,
          createdAt: '2024-01-05T08:00:00Z',
          lastActivity: '2024-09-15T16:20:00Z'
        }
      ];

      const mockStats: DashboardStats = {
        totalDistributors: 4,
        activeDistributors: 4,
        totalSales: 10100000000, // 101억
        totalCommissions: 2730000000, // 27.3억
        totalNodesSold: 16,
        pendingPayouts: 150000000, // 1.5억
        tierDistribution: {
          dealer: 1,
          agency: 1,
          distributor: 2
        }
      };

      setDistributors(mockDistributors);
      setDashboardStats(mockStats);
      setLoading(false);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'DISTRIBUTOR': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'AGENCY': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'DEALER': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'DISTRIBUTOR': return <Crown className="w-4 h-4" />;
      case 'AGENCY': return <Building2 className="w-4 h-4" />;
      case 'DEALER': return <UserCheck className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  // 코드 검색 함수
  const searchByCode = () => {
    if (!codeSearchTerm.trim()) {
      setCodeSearchResults([]);
      return;
    }

    const results = distributors.filter(distributor => {
      const searchLower = codeSearchTerm.toLowerCase();
      
      switch (codeSearchType) {
        case 'foundation':
          return distributor.foundationCode.toLowerCase().includes(searchLower);
        case 'personal':
          return distributor.personalCode.toLowerCase().includes(searchLower);
        case 'all':
        default:
          return distributor.foundationCode.toLowerCase().includes(searchLower) ||
                 distributor.personalCode.toLowerCase().includes(searchLower) ||
                 distributor.walletAddress.toLowerCase().includes(searchLower);
      }
    });

    setCodeSearchResults(results);
  };

  // 추천인 트리 구조 생성
  const buildReferralTree = (rootDistributor: Distributor): ReferralHierarchy => {
    // Mock 데이터로 트리 구조 생성 (실제로는 API에서 가져옴)
    const children: ReferralHierarchy[] = distributors
      .filter(d => d.id !== rootDistributor.id && Math.random() > 0.7) // Mock 조건
      .slice(0, 3)
      .map(child => ({
        distributor: child,
        children: [],
        level: 1,
        totalSales: child.totalSales,
        totalCommissions: child.totalCommissions
      }));

    return {
      distributor: rootDistributor,
      children,
      level: 0,
      totalSales: rootDistributor.totalSales + children.reduce((sum, child) => sum + child.totalSales, 0),
      totalCommissions: rootDistributor.totalCommissions + children.reduce((sum, child) => sum + child.totalCommissions, 0)
    };
  };

  const filteredDistributors = distributors.filter(distributor => {
    const matchesSearch = distributor.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         distributor.walletAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         distributor.personalCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = tierFilter === 'all' || distributor.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* 헤더 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Users className="w-8 h-8 text-orange-500" />
              <span>레퍼럴 관리 시스템</span>
            </h1>
            <p className="text-gray-600 mt-1">총판 구조 기반 레퍼럴 시스템 관리</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>총판 추가</span>
            </button>
            <button
              onClick={() => setShowHierarchyModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <TreePine className="w-4 h-4" />
              <span>하이어라키 보기</span>
            </button>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'dashboard', label: '대시보드', icon: BarChart3 },
              { id: 'code-search', label: '코드 조회', icon: Search },
              { id: 'distributors', label: '총판 관리', icon: Users },
              { id: 'commissions', label: '수수료 관리', icon: DollarSign },
              { id: 'hierarchy', label: '하이어라키', icon: TreePine },
              { id: 'boom-events', label: 'BOOM 이벤트', icon: Zap },
              { id: 'analytics', label: '성과 분석', icon: TrendingUp }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* 대시보드 탭 */}
          {activeTab === 'dashboard' && dashboardStats && (
            <div className="space-y-6">
              {/* 통계 카드 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">총 총판 수</p>
                      <p className="text-3xl font-bold">{dashboardStats.totalDistributors}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">총 매출</p>
                      <p className="text-3xl font-bold">{formatCurrency(dashboardStats.totalSales)}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">총 수수료</p>
                      <p className="text-3xl font-bold">{formatCurrency(dashboardStats.totalCommissions)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-purple-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">노드 판매</p>
                      <p className="text-3xl font-bold">{dashboardStats.totalNodesSold}개</p>
                    </div>
                    <Target className="w-8 h-8 text-orange-200" />
                  </div>
                </div>
              </div>

              {/* 등급별 분포 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">등급별 분포</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Crown className="w-5 h-5 text-purple-500" />
                        <span className="font-medium">총판</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ width: `${(dashboardStats.tierDistribution.distributor / dashboardStats.totalDistributors) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{dashboardStats.tierDistribution.distributor}명</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Building2 className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">대리점</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${(dashboardStats.tierDistribution.agency / dashboardStats.totalDistributors) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{dashboardStats.tierDistribution.agency}명</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <UserCheck className="w-5 h-5 text-green-500" />
                        <span className="font-medium">딜러</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${(dashboardStats.tierDistribution.dealer / dashboardStats.totalDistributors) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{dashboardStats.tierDistribution.dealer}명</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">최근 활동</h3>
                  <div className="space-y-3">
                    {distributors.slice(0, 5).map((distributor) => (
                      <div key={distributor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {getTierIcon(distributor.tier)}
                          <div>
                            <p className="font-medium text-gray-900">{distributor.nickname}</p>
                            <p className="text-sm text-gray-500">{formatAddress(distributor.walletAddress)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{formatCurrency(distributor.totalSales)}</p>
                          <p className="text-xs text-gray-500">{new Date(distributor.lastActivity).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 코드 조회 탭 */}
          {activeTab === 'code-search' && (
            <div className="space-y-6">
              {/* 검색 헤더 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Search className="w-6 h-6 text-orange-500" />
                  <span>추천인 코드 조회</span>
                </h2>
                <p className="text-gray-600 mb-6">파운데이션 코드 또는 개인 코드로 총판을 검색하고 추천인 구조를 확인하세요.</p>
                
                {/* 검색 폼 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">검색 코드</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="코드를 입력하세요 (예: A8888888, 12345678)"
                        value={codeSearchTerm}
                        onChange={(e) => setCodeSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchByCode()}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">검색 타입</label>
                    <select
                      value={codeSearchType}
                      onChange={(e) => setCodeSearchType(e.target.value as 'foundation' | 'personal' | 'all')}
                      className="w-full border border-gray-300 rounded-lg px-3 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="all">전체</option>
                      <option value="foundation">파운데이션 코드</option>
                      <option value="personal">개인 코드</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={searchByCode}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                      <span>검색</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 검색 결과 */}
              {codeSearchResults.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      검색 결과 ({codeSearchResults.length}건)
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">총판 정보</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">코드</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">등급</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">매출</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수수료</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">추천인</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {codeSearchResults.map((distributor) => (
                          <tr key={distributor.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                {getTierIcon(distributor.tier)}
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{distributor.nickname}</div>
                                  <div className="text-sm text-gray-500">{formatAddress(distributor.walletAddress)}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="space-y-1">
                                <div className="text-sm text-gray-900">
                                  <span className="font-medium">파운데이션:</span> {distributor.foundationCode}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <span className="font-medium">개인:</span> {distributor.personalCode}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTierColor(distributor.tier)}`}>
                                {distributor.tier}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(distributor.totalSales)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(distributor.totalCommissions)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="space-y-1">
                                <div>직접: {distributor.directReferrals}명</div>
                                <div>전체: {distributor.totalReferrals}명</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedCodeResult(distributor);
                                  setShowCodeTreeModal(true);
                                }}
                                className="text-orange-600 hover:text-orange-900 flex items-center space-x-1"
                              >
                                <TreePine className="w-4 h-4" />
                                <span>트리 보기</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 검색 결과가 없을 때 */}
              {codeSearchTerm && codeSearchResults.length === 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">검색 결과가 없습니다</h3>
                  <p className="text-gray-500">
                    입력하신 코드 "{codeSearchTerm}"에 해당하는 총판을 찾을 수 없습니다.
                  </p>
                </div>
              )}

              {/* 초기 상태 */}
              {!codeSearchTerm && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">코드를 입력하여 검색하세요</h3>
                  <p className="text-gray-500 mb-4">
                    파운데이션 코드(A8888888) 또는 개인 코드(지갑 뒷 8자리)로 총판을 검색할 수 있습니다.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto text-left">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">파운데이션 코드</h4>
                      <p className="text-sm text-gray-600">예: A8888888</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">개인 코드</h4>
                      <p className="text-sm text-gray-600">예: 12345678</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 총판 관리 탭 */}
          {activeTab === 'distributors' && (
            <div className="space-y-6">
              {/* 필터 및 검색 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="총판 검색..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <select
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="all">모든 등급</option>
                    <option value="DISTRIBUTOR">총판</option>
                    <option value="AGENCY">대리점</option>
                    <option value="DEALER">딜러</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      viewMode === 'list' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    리스트
                  </button>
                  <button
                    onClick={() => setViewMode('tree')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      viewMode === 'tree' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    트리
                  </button>
                </div>
              </div>

              {/* 총판 목록 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">총판 정보</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">등급</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">매출</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수수료</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">추천인</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">노드</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredDistributors.map((distributor) => (
                        <tr key={distributor.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                {getTierIcon(distributor.tier)}
                              </div>
                              <div>
                                <button 
                                  onClick={() => window.open(`http://localhost:3600/admin/user-management?user=${distributor.id}`, '_blank')}
                                  className="font-medium text-gray-900 hover:text-orange-600 transition-colors cursor-pointer"
                                >
                                  {distributor.nickname}
                                </button>
                                <div className="text-sm text-gray-500 font-mono">{formatAddress(distributor.walletAddress)}</div>
                                <div className="text-xs text-gray-400">코드: {distributor.personalCode}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getTierColor(distributor.tier)}`}>
                              {getTierIcon(distributor.tier)}
                              <span>{distributor.tier}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(distributor.totalSales)}</div>
                            <div className="text-xs text-gray-500">수수료율: {(distributor.commissionRate * 100).toFixed(1)}%</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(distributor.totalCommissions)}</div>
                            <div className="text-xs text-gray-500">USDT: {formatCurrency(distributor.totalCommissions * 0.4)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button 
                              onClick={() => window.open(`http://localhost:3600/admin/referral-management?tab=referrals&distributor=${distributor.id}`, '_blank')}
                              className="text-sm font-medium text-gray-900 hover:text-orange-600 transition-colors cursor-pointer"
                            >
                              {distributor.totalReferrals}명
                            </button>
                            <div className="text-xs text-gray-500">직접: {distributor.directReferrals}명</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">{distributor.nodeRewards}개</span>
                              {distributor.carEligible && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  <Server className="w-3 h-3 mr-1" />
                                  노드
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => window.open(`http://localhost:3600/admin/user-management?user=${distributor.id}&tab=edit`, '_blank')}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                수정
                              </button>
                              <button
                                onClick={() => window.open(`http://localhost:3600/admin/referral-management?tab=commissions&distributor=${distributor.id}`, '_blank')}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100"
                              >
                                <DollarSign className="w-3 h-3 mr-1" />
                                수수료
                              </button>
                              <button
                                onClick={() => window.open(`http://localhost:3600/admin/referral-management?tab=bonus&distributor=${distributor.id}`, '_blank')}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100"
                              >
                                <Gift className="w-3 h-3 mr-1" />
                                보너스
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 수수료 관리 탭 */}
          {activeTab === 'commissions' && (
            <div className="space-y-6">
              {/* 수수료 통계 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">총 수수료</p>
                      <p className="text-2xl font-bold">{formatCurrency(2730000000)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">대기 중</p>
                      <p className="text-2xl font-bold">{formatCurrency(150000000)}</p>
                    </div>
                    <Clock3 className="w-8 h-8 text-blue-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">지급 완료</p>
                      <p className="text-2xl font-bold">{formatCurrency(2580000000)}</p>
                    </div>
                    <CheckCircle2 className="w-8 h-8 text-purple-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">평균 수수료율</p>
                      <p className="text-2xl font-bold">27.0%</p>
                    </div>
                    <Percent className="w-8 h-8 text-orange-200" />
                  </div>
                </div>
              </div>

              {/* 수수료 상세 내역 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">수수료 상세 내역</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">총판</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">매출</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">수수료율</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">USDT</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VTTN</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">액션</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {distributors.map((distributor) => (
                        <tr key={distributor.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                {getTierIcon(distributor.tier)}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{distributor.nickname}</div>
                                <div className="text-sm text-gray-500">{formatAddress(distributor.walletAddress)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(distributor.totalSales)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {(distributor.commissionRate * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(distributor.totalCommissions * 0.4)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatCurrency(distributor.totalCommissions * 0.4)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              지급완료
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100">
                                <Edit className="w-3 h-3 mr-1" />
                                조정
                              </button>
                              <button className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100">
                                <DollarSign className="w-3 h-3 mr-1" />
                                지급
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 수수료 조정 패널 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">수수료 조정</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">기본 수수료율</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">딜러</span>
                        <span className="text-sm font-bold text-green-600">10%</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">대리점</span>
                        <span className="text-sm font-bold text-blue-600">20%</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">총판</span>
                        <span className="text-sm font-bold text-purple-600">30%</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">조정 범위</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">딜러</span>
                        <span className="text-sm text-gray-600">5% ~ 15%</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">대리점</span>
                        <span className="text-sm text-gray-600">15% ~ 25%</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">총판</span>
                        <span className="text-sm text-gray-600">25% ~ 35%</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">보너스 지급</h4>
                    <div className="space-y-3">
                      <button className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors">
                        <div className="text-sm font-medium text-yellow-800">월간 최고 매출</div>
                        <div className="text-xs text-yellow-600">500만원 보너스</div>
                      </button>
                      <button className="w-full p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
                        <div className="text-sm font-medium text-green-800">신규 추천인</div>
                        <div className="text-xs text-green-600">100만원 보너스</div>
                      </button>
                      <button className="w-full p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                        <div className="text-sm font-medium text-blue-800">노드 판매</div>
                        <div className="text-xs text-blue-600">노드당 50만원</div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 하이어라키 탭 */}
          {activeTab === 'hierarchy' && (
            <div className="space-y-6">
              {/* 하이어라키 컨트롤 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">총판 하이어라키 구조</h3>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          viewMode === 'list' 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        리스트
                      </button>
                      <button
                        onClick={() => setViewMode('tree')}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          viewMode === 'tree' 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        트리
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="px-3 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600">
                        <Search className="w-4 h-4" />
                      </button>
                      <button className="px-3 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 하이어라키 시각화 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-auto">
                <div className="min-h-[600px] relative">
                  {/* VOLTRON 재단 */}
                  <div className="flex justify-center mb-8">
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 text-white text-center shadow-lg cursor-pointer hover:shadow-xl transition-all">
                      <div className="flex items-center justify-center space-x-3 mb-2">
                        <Crown className="w-8 h-8" />
                        <h4 className="text-xl font-bold">VOLTRON 재단</h4>
                      </div>
                      <p className="text-purple-100 text-sm">코드: A8888888</p>
                      <p className="text-purple-100 text-sm">총 매출: {formatCurrency(10100000000)}</p>
                    </div>
                  </div>

                  {/* 연결선 */}
                  <div className="flex justify-center mb-8">
                    <div className="w-px h-8 bg-gray-300"></div>
                  </div>

                  {/* 총판 레벨 - 4개 모두 표시 */}
                  <div className="flex justify-center space-x-8 mb-8">
                    {distributors.map((distributor) => (
                      <div 
                        key={distributor.id} 
                        className={`rounded-lg p-4 text-white text-center shadow-md cursor-pointer hover:shadow-xl transition-all transform hover:scale-105 ${
                          distributor.tier === 'DISTRIBUTOR' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                          distributor.tier === 'AGENCY' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                          'bg-gradient-to-r from-orange-500 to-orange-600'
                        }`}
                        onClick={() => window.open(`http://localhost:3600/admin/user-management?user=${distributor.id}`, '_blank')}
                        onMouseEnter={(e) => {
                          // 호버 시 상세 정보 툴팁 표시
                          const tooltip = document.createElement('div');
                          tooltip.className = 'absolute bg-gray-900 text-white p-3 rounded-lg shadow-lg z-50';
                          tooltip.innerHTML = `
                            <div class="text-sm">
                              <div class="font-bold mb-2">${distributor.nickname}</div>
                              <div>최근 매출: ${formatCurrency(distributor.totalSales)}</div>
                              <div>추천인 수: ${distributor.totalReferrals}명</div>
                              <div>수수료 지급율: ${(distributor.commissionRate * 100).toFixed(1)}%</div>
                              <div>노드 판매율: ${distributor.nodeRewards}개</div>
                            </div>
                          `;
                          tooltip.style.left = e.pageX + 10 + 'px';
                          tooltip.style.top = e.pageY - 10 + 'px';
                          document.body.appendChild(tooltip);
                          (e.target as any).tooltip = tooltip;
                        }}
                        onMouseLeave={(e) => {
                          if ((e.target as any).tooltip) {
                            document.body.removeChild((e.target as any).tooltip);
                            (e.target as any).tooltip = null;
                          }
                        }}
                      >
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          {distributor.tier === 'DISTRIBUTOR' ? <Crown className="w-5 h-5" /> :
                           distributor.tier === 'AGENCY' ? <Building2 className="w-5 h-5" /> :
                           <UserCheck className="w-5 h-5" />}
                          <h5 className="font-bold">{distributor.nickname}</h5>
                        </div>
                        <p className="text-xs opacity-90">코드: {distributor.personalCode}</p>
                        <p className="text-xs opacity-90">매출: {formatCurrency(distributor.totalSales)}</p>
                        <p className="text-xs opacity-90">추천인: {distributor.totalReferrals}명</p>
                        <div className="mt-2 flex items-center justify-center space-x-1">
                          <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                            {distributor.tier}
                          </span>
                          {distributor.carEligible && (
                            <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-1 rounded">
                              <Server className="w-3 h-3 inline mr-1" />
                              노드
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 연결선들 */}
                  <div className="flex justify-center space-x-8 mb-8">
                    {distributors.map((_, index) => (
                      <div key={index} className="w-px h-8 bg-gray-300"></div>
                    ))}
                  </div>

                  {/* 하위 레벨들 (접기/펼치기 가능) */}
                  <div className="flex justify-center space-x-8">
                    {distributors.map((distributor) => (
                      <div key={`sub-${distributor.id}`} className="text-center">
                        <div className="w-px h-8 bg-gray-300 mx-auto mb-4"></div>
                        <div className="bg-gray-100 rounded-lg p-3 text-gray-700 text-sm">
                          <div className="font-medium">하위 {distributor.directReferrals}명</div>
                          <div className="text-xs">직접 추천인</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 하이어라키 통계 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">등급별 분포</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Crown className="w-5 h-5 text-purple-500" />
                        <span className="font-medium">총판</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div className="bg-purple-500 h-2 rounded-full" style={{ width: '50%' }}></div>
                        </div>
                        <span className="text-sm font-medium">2명</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Building2 className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">대리점</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-500 h-2 rounded-full" style={{ width: '25%' }}></div>
                        </div>
                        <span className="text-sm font-medium">1명</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <UserCheck className="w-5 h-5 text-green-500" />
                        <span className="font-medium">딜러</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: '25%' }}></div>
                        </div>
                        <span className="text-sm font-medium">1명</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">매출 분포</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">총판</span>
                      <span className="text-sm font-bold text-purple-600">{formatCurrency(7700000000)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">대리점</span>
                      <span className="text-sm font-bold text-blue-600">{formatCurrency(1800000000)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">딜러</span>
                      <span className="text-sm font-bold text-green-600">{formatCurrency(600000000)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold">총합</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(10100000000)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">추천인 분포</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">총판</span>
                      <span className="text-sm font-bold text-purple-600">330명</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">대리점</span>
                      <span className="text-sm font-bold text-blue-600">75명</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">딜러</span>
                      <span className="text-sm font-bold text-green-600">25명</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold">총합</span>
                        <span className="text-sm font-bold text-gray-900">430명</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'nodes-old' && (
            <div className="space-y-6">
              {/* 노드 판매 통계 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">총 노드 판매</p>
                      <p className="text-3xl font-bold">16개</p>
                    </div>
                    <Server className="w-8 h-8 text-blue-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">총 판매액</p>
                      <p className="text-2xl font-bold">{formatCurrency(8000000000)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">총 인센티브</p>
                      <p className="text-2xl font-bold">{formatCurrency(1200000000)}</p>
                    </div>
                    <Gift className="w-8 h-8 text-purple-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">평균 인센티브율</p>
                      <p className="text-2xl font-bold">15%</p>
                    </div>
                    <Percent className="w-8 h-8 text-orange-200" />
                  </div>
                </div>
              </div>

              {/* 노드 판매 인센티브 구조 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">노드 판매 인센티브 구조</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-green-800">1개 판매</h4>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-green-700">인센티브율: <span className="font-bold">10%</span></p>
                      <p className="text-sm text-green-700">USDT: <span className="font-bold">30%</span></p>
                      <p className="text-sm text-green-700">VTTN: <span className="font-bold">70%</span></p>
                      <p className="text-sm text-green-700">예시: 5억원 판매 시</p>
                      <p className="text-sm text-green-700">→ 5천만원 인센티브</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-blue-800">5개 판매</h4>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-blue-700">인센티브율: <span className="font-bold">20%</span></p>
                      <p className="text-sm text-blue-700">USDT: <span className="font-bold">30%</span></p>
                      <p className="text-sm text-blue-700">VTTN: <span className="font-bold">70%</span></p>
                      <p className="text-sm text-blue-700">예시: 25억원 판매 시</p>
                      <p className="text-sm text-blue-700">→ 5억원 인센티브</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-purple-800">10개 판매</h4>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-purple-700">인센티브율: <span className="font-bold">30%</span></p>
                      <p className="text-sm text-purple-700">USDT: <span className="font-bold">30%</span></p>
                      <p className="text-sm text-purple-700">VTTN: <span className="font-bold">70%</span></p>
                      <p className="text-sm text-purple-700">예시: 50억원 판매 시</p>
                      <p className="text-sm text-purple-700">→ 15억원 인센티브</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 노드 판매 현황 */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">노드 판매 현황</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">판매자</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">구매자</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">노드 수</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">판매액</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">인센티브</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Crown className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">총판 김철수</div>
                              <div className="text-sm text-gray-500">{formatAddress('0x1234...7890')}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">고객 A</div>
                          <div className="text-sm text-gray-500">{formatAddress('0x2345...8901')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            3개
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(1500000000)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(300000000)}</div>
                          <div className="text-xs text-gray-500">20% 인센티브</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            완료
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">2024-09-15</div>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Crown className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">총판 정수진</div>
                              <div className="text-sm text-gray-500">{formatAddress('0x4567...0123')}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">고객 B</div>
                          <div className="text-sm text-gray-500">{formatAddress('0x5678...1234')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            5개
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(2500000000)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(500000000)}</div>
                          <div className="text-xs text-gray-500">20% 인센티브</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            완료
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">2024-09-14</div>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Building2 className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">대리점 이영희</div>
                              <div className="text-sm text-gray-500">{formatAddress('0x2345...8901')}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">고객 C</div>
                          <div className="text-sm text-gray-500">{formatAddress('0x6789...2345')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            1개
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(500000000)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(50000000)}</div>
                          <div className="text-xs text-gray-500">10% 인센티브</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock3 className="w-3 h-3 mr-1" />
                            대기중
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">2024-09-15</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 노드 판매 관리 액션 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">노드 판매 관리</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">인센티브 지급</h4>
                    <div className="space-y-3">
                      <button className="w-full p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-green-800">대기 중인 인센티브</div>
                            <div className="text-xs text-green-600">3건, 총 {formatCurrency(50000000)}</div>
                          </div>
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                      </button>
                      <button className="w-full p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-blue-800">일괄 지급</div>
                            <div className="text-xs text-blue-600">모든 대기 중인 인센티브 지급</div>
                          </div>
                          <DollarSign className="w-5 h-5 text-blue-600" />
                        </div>
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">노드 판매 설정</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">노드 가격</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(500000000)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">최소 판매 수량</span>
                        <span className="text-sm font-bold text-gray-900">1개</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium">최대 판매 수량</span>
                        <span className="text-sm font-bold text-gray-900">10개</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BOOM 이벤트 관리 탭 */}
          {activeTab === 'boom-events' && (
            <div className="space-y-6">
              {/* BOOM 이벤트 설정 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-orange-500" />
                  BOOM 이벤트 모바일 노출 설정
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-3">이벤트별 모바일 노출 설정</h4>
                    <div className="space-y-3">
                      {/* BOOM 1차 이벤트 */}
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">1</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">BOOM 1차 이벤트</p>
                            <p className="text-sm text-gray-600">참가비: 30 VTTN | 상금: 30,000 VTTN</p>
                            <p className="text-xs text-blue-600">오늘 오후 6시 시작, 2일 후 오후 6시 종료</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={boomEventSettings.boom1Visible}
                              onChange={(e) => setBoomEventSettings(prev => ({
                                ...prev,
                                boom1Visible: e.target.checked
                              }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                          </label>
                          <span className="text-xs text-gray-500">
                            {boomEventSettings.boom1Visible ? '보기' : '가리기'}
                          </span>
                        </div>
                      </div>

                      {/* BOOM 2차 이벤트 */}
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">2</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">BOOM 2차 이벤트</p>
                            <p className="text-sm text-gray-600">참가비: 70 VTTN | 상금: 50,000 VTTN</p>
                            <p className="text-xs text-gray-500">시작일 미정</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={boomEventSettings.boom2Visible}
                              onChange={(e) => setBoomEventSettings(prev => ({
                                ...prev,
                                boom2Visible: e.target.checked
                              }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                          </label>
                          <span className="text-xs text-gray-500">
                            {boomEventSettings.boom2Visible ? '보기' : '가리기'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 설정 저장 버튼 */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        // API 호출로 설정 저장
                        console.log('BOOM 이벤트 설정 저장:', boomEventSettings);
                        alert('BOOM 이벤트 설정이 저장되었습니다.');
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      설정 저장
                    </button>
                  </div>
                </div>
              </div>

              {/* 경품 지급 구조 관리 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <Gift className="w-5 h-5 mr-2 text-orange-500" />
                  경품 지급 구조 관리
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <Gift className="w-5 h-5 mr-2 text-yellow-600" />
                      현재 경품 지급 구조
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-yellow-100 rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">1</span>
                          </div>
                          <span className="font-medium">1등</span>
                        </div>
                        <span className="text-orange-600 font-bold">30,000 USDT(VTTN)</span>
                      </div>
                      <div className="flex items-center justify-between bg-gray-100 rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">2</span>
                          </div>
                          <span className="font-medium">2등~11등</span>
                        </div>
                        <span className="text-gray-700 font-bold">2,000 USDT(VTTN)</span>
                      </div>
                      <div className="flex items-center justify-between bg-orange-100 rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">3</span>
                          </div>
                          <span className="font-medium">12등~50등</span>
                        </div>
                        <span className="text-orange-600 font-bold">1,000 USDT(VTTN)</span>
                      </div>
                      <div className="flex items-center justify-between bg-purple-100 rounded-lg p-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                            <Star className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium">다추천상(10명선정)</span>
                        </div>
                        <span className="text-purple-600 font-bold">1,000 USDT(VTTN)</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">경품 구조 수정</h4>
                    <p className="text-sm text-blue-600">
                      경품 지급 구조는 시스템 관리자만 수정할 수 있습니다. 
                      수정이 필요한 경우 개발팀에 문의하세요.
                    </p>
                  </div>
                </div>
              </div>

              {/* 노드 구독 상품 관리 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Server className="w-5 h-5 mr-2 text-orange-500" />
                    노드 구독 상품 관리
                  </h3>
                  <button
                    onClick={() => {
                      // 새 노드 상품 추가 모달 열기
                      alert('새 노드 상품 추가 기능을 구현합니다.');
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>상품 추가</span>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* 노드 상품 목록 */}
                  <div className="grid gap-4">
                    {/* Basic 노드 */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Server className="w-6 h-6" />
                          </div>
                          <div>
                            <h4 className="text-xl font-bold">Basic 노드</h4>
                            <p className="text-blue-100">CPU: 2코어 | RAM: 4GB | Storage: 100GB</p>
                            <p className="text-blue-100">월 수익: 50-80 VTTN</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">100 VTTN</p>
                          <p className="text-blue-100">월 구독료</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              // 노드 상품 수정 모달 열기
                              alert('Basic 노드 수정 기능을 구현합니다.');
                            }}
                            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Basic 노드를 삭제하시겠습니까?')) {
                                alert('Basic 노드가 삭제되었습니다.');
                              }
                            }}
                            className="p-2 bg-red-500 bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Pro 노드 */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Server className="w-6 h-6" />
                          </div>
                          <div>
                            <h4 className="text-xl font-bold">Pro 노드</h4>
                            <p className="text-green-100">CPU: 4코어 | RAM: 8GB | Storage: 250GB</p>
                            <p className="text-green-100">월 수익: 120-180 VTTN</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">250 VTTN</p>
                          <p className="text-green-100">월 구독료</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              // 노드 상품 수정 모달 열기
                              alert('Pro 노드 수정 기능을 구현합니다.');
                            }}
                            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Pro 노드를 삭제하시겠습니까?')) {
                                alert('Pro 노드가 삭제되었습니다.');
                              }
                            }}
                            className="p-2 bg-red-500 bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Enterprise 노드 */}
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Server className="w-6 h-6" />
                          </div>
                          <div>
                            <h4 className="text-xl font-bold">Enterprise 노드</h4>
                            <p className="text-purple-100">CPU: 8코어 | RAM: 16GB | Storage: 500GB</p>
                            <p className="text-purple-100">월 수익: 250-350 VTTN</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">500 VTTN</p>
                          <p className="text-purple-100">월 구독료</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              // 노드 상품 수정 모달 열기
                              alert('Enterprise 노드 수정 기능을 구현합니다.');
                            }}
                            className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('Enterprise 노드를 삭제하시겠습니까?')) {
                                alert('Enterprise 노드가 삭제되었습니다.');
                              }
                            }}
                            className="p-2 bg-red-500 bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 성과 분석 탭 */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* 성과 지표 카드 */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-100 text-sm font-medium">최고 성과자</p>
                      <p className="text-xl font-bold">총판 정수진</p>
                      <p className="text-yellow-100 text-xs">{formatCurrency(4200000000)}</p>
                    </div>
                    <Award className="w-8 h-8 text-yellow-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm font-medium">최고 수수료</p>
                      <p className="text-xl font-bold">총판 정수진</p>
                      <p className="text-green-100 text-xs">{formatCurrency(1260000000)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">최다 추천인</p>
                      <p className="text-xl font-bold">총판 정수진</p>
                      <p className="text-blue-100 text-xs">180명</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-200" />
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">평균 성과</p>
                      <p className="text-xl font-bold">{formatCurrency(2525000000)}</p>
                      <p className="text-purple-100 text-xs">총판당 평균</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-purple-200" />
                  </div>
                </div>
              </div>

              {/* 매출 비교 차트 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">매출 비교</h3>
                <div className="space-y-4">
                  {distributors
                    .sort((a, b) => b.totalSales - a.totalSales)
                    .map((distributor, index) => {
                      const maxSales = Math.max(...distributors.map(d => d.totalSales));
                      const percentage = (distributor.totalSales / maxSales) * 100;
                      return (
                        <div key={distributor.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                              <div className="p-2 bg-orange-100 rounded-lg">
                                {getTierIcon(distributor.tier)}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{distributor.nickname}</div>
                                <div className="text-sm text-gray-500">{distributor.tier}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-gray-900">{formatCurrency(distributor.totalSales)}</div>
                              <div className="text-xs text-gray-500">{(percentage).toFixed(1)}%</div>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* 수수료 분석 차트 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">수수료 분석</h3>
                <div className="space-y-4">
                  {distributors
                    .sort((a, b) => b.totalCommissions - a.totalCommissions)
                    .map((distributor, index) => {
                      const maxCommissions = Math.max(...distributors.map(d => d.totalCommissions));
                      const percentage = (distributor.totalCommissions / maxCommissions) * 100;
                      return (
                        <div key={distributor.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-500 w-6">#{index + 1}</span>
                              <div className="p-2 bg-green-100 rounded-lg">
                                {getTierIcon(distributor.tier)}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{distributor.nickname}</div>
                                <div className="text-sm text-gray-500">{(distributor.commissionRate * 100).toFixed(1)}% 수수료율</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-gray-900">{formatCurrency(distributor.totalCommissions)}</div>
                              <div className="text-xs text-gray-500">{(percentage).toFixed(1)}%</div>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* 상세 성과 분석 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">등급별 성과</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Crown className="w-5 h-5 text-purple-600" />
                        <div>
                          <div className="font-medium text-purple-900">총판</div>
                          <div className="text-sm text-purple-600">2명</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-purple-900">{formatCurrency(7700000000)}</div>
                        <div className="text-sm text-purple-600">평균: {formatCurrency(3850000000)}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium text-blue-900">대리점</div>
                          <div className="text-sm text-blue-600">1명</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-blue-900">{formatCurrency(1800000000)}</div>
                        <div className="text-sm text-blue-600">평균: {formatCurrency(1800000000)}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <UserCheck className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="font-medium text-green-900">딜러</div>
                          <div className="text-sm text-green-600">1명</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-900">{formatCurrency(600000000)}</div>
                        <div className="text-sm text-green-600">평균: {formatCurrency(600000000)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">추천인 성과</h4>
                  <div className="space-y-4">
                    {distributors
                      .sort((a, b) => b.totalReferrals - a.totalReferrals)
                      .map((distributor) => (
                        <div key={distributor.id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              {getTierIcon(distributor.tier)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{distributor.nickname}</div>
                              <div className="text-sm text-gray-500">직접: {distributor.directReferrals}명</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900">{distributor.totalReferrals}명</div>
                            <div className="text-sm text-gray-500">총 추천인</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* 성과 요약 */}
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-orange-900 mb-4">전체 성과 요약</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-900">{formatCurrency(10100000000)}</div>
                    <div className="text-sm text-orange-700">총 매출</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-900">{formatCurrency(2730000000)}</div>
                    <div className="text-sm text-orange-700">총 수수료</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-900">430명</div>
                    <div className="text-sm text-orange-700">총 추천인</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 총판 추가 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">새 총판 추가</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">지갑 주소</label>
                <input
                  type="text"
                  value={newDistributor.walletAddress}
                  onChange={(e) => setNewDistributor({ ...newDistributor, walletAddress: e.target.value })}
                  placeholder="0x..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">닉네임</label>
                <input
                  type="text"
                  value={newDistributor.nickname}
                  onChange={(e) => setNewDistributor({ ...newDistributor, nickname: e.target.value })}
                  placeholder="총판 닉네임"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">등급</label>
                <select
                  value={newDistributor.tier}
                  onChange={(e) => setNewDistributor({ ...newDistributor, tier: e.target.value as any })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="DEALER">딜러</option>
                  <option value="AGENCY">대리점</option>
                  <option value="DISTRIBUTOR">총판</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">수수료율 (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={newDistributor.commissionRate * 100}
                  onChange={(e) => setNewDistributor({ ...newDistributor, commissionRate: parseFloat(e.target.value) / 100 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => {
                  // 총판 추가 로직
                  setShowCreateModal(false);
                }}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 추천인 트리 모달 */}
      {showCodeTreeModal && selectedCodeResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <TreePine className="w-6 h-6 text-orange-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">추천인 트리 구조</h3>
                  <p className="text-sm text-gray-500">
                    {selectedCodeResult.nickname} ({selectedCodeResult.foundationCode})
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCodeTreeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* 선택된 총판 정보 */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {getTierIcon(selectedCodeResult.tier)}
                  </div>
                  <div className="flex-1">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">{selectedCodeResult.nickname}</h4>
                        <p className="text-sm text-gray-600">{formatAddress(selectedCodeResult.walletAddress)}</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTierColor(selectedCodeResult.tier)} mt-1`}>
                          {selectedCodeResult.tier}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">파운데이션 코드</div>
                        <div className="font-medium text-gray-900">{selectedCodeResult.foundationCode}</div>
                        <div className="text-sm text-gray-600 mt-1">개인 코드</div>
                        <div className="font-medium text-gray-900">{selectedCodeResult.personalCode}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">총 매출</div>
                        <div className="font-medium text-gray-900">{formatCurrency(selectedCodeResult.totalSales)}</div>
                        <div className="text-sm text-gray-600 mt-1">총 수수료</div>
                        <div className="font-medium text-gray-900">{formatCurrency(selectedCodeResult.totalCommissions)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 트리 구조 */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <TreePine className="w-5 h-5 text-orange-500" />
                  <span>추천인 구조</span>
                </h4>
                
                {(() => {
                  const tree = buildReferralTree(selectedCodeResult);
                  
                  const renderTreeNode = (node: ReferralHierarchy, isLast: boolean = false, prefix: string = '') => (
                    <div key={node.distributor.id} className="space-y-2">
                      <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="text-gray-400 font-mono text-sm">
                          {prefix}{isLast ? '└─' : '├─'}
                        </div>
                        <div className="flex-shrink-0">
                          {getTierIcon(node.distributor.tier)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900">{node.distributor.nickname}</div>
                              <div className="text-sm text-gray-500">
                                {formatAddress(node.distributor.walletAddress)} • {node.distributor.foundationCode}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(node.distributor.totalSales)}
                              </div>
                              <div className="text-xs text-gray-500">
                                추천인 {node.distributor.totalReferrals}명
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {node.children.length > 0 && (
                        <div className="ml-8">
                          {node.children.map((child, index) => 
                            renderTreeNode(
                              child, 
                              index === node.children.length - 1,
                              prefix + (isLast ? '  ' : '│ ')
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );

                  return tree ? renderTreeNode(tree) : (
                    <div className="text-center py-8 text-gray-500">
                      <TreePine className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>추천인 구조 데이터를 불러올 수 없습니다.</p>
                    </div>
                  );
                })()}
              </div>

              {/* 통계 요약 */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">구조 통계</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-blue-600 text-sm font-medium">직접 추천인</div>
                    <div className="text-2xl font-bold text-blue-900">{selectedCodeResult.directReferrals}</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-green-600 text-sm font-medium">전체 추천인</div>
                    <div className="text-2xl font-bold text-green-900">{selectedCodeResult.totalReferrals}</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="text-purple-600 text-sm font-medium">총 매출</div>
                    <div className="text-2xl font-bold text-purple-900">{formatCurrency(selectedCodeResult.totalSales)}</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="text-orange-600 text-sm font-medium">총 수수료</div>
                    <div className="text-2xl font-bold text-orange-900">{formatCurrency(selectedCodeResult.totalCommissions)}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowCodeTreeModal(false)}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default ReferralManagementPage;
